import {
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import React from "react";
// IMPORTACIÓN CORREGIDA:
import { RasReportRow } from "../types/rasTypes";

interface RasReportTableProps {
  dataRas: RasReportRow[]; // Usamos el nuevo tipo unificado
}

// Formatea un número a 2 decimales; vacío/indefinido -> "0"
const fmt = (value: number | undefined | null): string => {
  if (value === undefined || value === null || isNaN(Number(value))) return "0";
  const num = Number(value);
  return Number.isInteger(num) ? num.toFixed(0) : num.toFixed(2);
};

// Placeholder para columnas Tier 3 (sin fuente de datos aún)
const PENDING = "—";

// Estilos compactos (basados en la tabla de Control de Producción) para que quepan
// todas las columnas sin scroll horizontal.
const headStyle = {
  position: "sticky" as const,
  top: 0,
  zIndex: 1,
  backgroundColor: "white",
  color: "#004F86",
  fontWeight: "bold",
  fontSize: "0.6rem",
  padding: "1px 2px",
  lineHeight: 1.1,
  textAlign: "center" as const,
  verticalAlign: "bottom" as const,
  whiteSpace: "normal" as const,
  wordBreak: "break-word" as const,
  borderRight: "1px solid #e0e0e0",
};
const bodyStyle = {
  fontSize: "0.6rem",
  padding: "1px 2px",
  textAlign: "center" as const,
  verticalAlign: "middle" as const,
  whiteSpace: "normal" as const,
  wordBreak: "break-word" as const,
  borderRight: "1px solid #f0f0f0",
};
// Columna "Actividad" un poco más ancha y alineada a la izquierda (texto largo)
const headActivity = { ...headStyle, minWidth: 90, textAlign: "left" as const };
const bodyActivity = { ...bodyStyle, textAlign: "left" as const };
// Columnas Tier 3 (placeholder), atenuadas
const bodyPending = { ...bodyStyle, color: "#b0b0b0", fontStyle: "italic" as const };

const RasReportTable: React.FC<RasReportTableProps> = ({ dataRas }) => {
  return (
    <Grid container sx={{ height: "100%" }}>
      <TableContainer
        component={Paper}
        sx={{
          width: "100%",
          // Altura responsiva: usa el alto disponible del viewport
          maxHeight: { xs: "55vh", md: "calc(100vh - 260px)" },
          overflow: "auto",
        }}
      >
        {/*
          Responsivo: en pantallas anchas la tabla llena el 100% del contenedor;
          por debajo de minWidth las columnas mantienen un ancho legible y el
          contenedor hace scroll horizontal en vez de aplastarse.
        */}
        <Table
          stickyHeader
          size="small"
          sx={{ width: "100%", minWidth: 1500, borderCollapse: "collapse" }}
        >
          <TableHead>
            <TableRow>
              <TableCell sx={headStyle}>Id</TableCell>
              <TableCell sx={headStyle}>Etapa</TableCell>
              <TableCell sx={headActivity}>Actividad</TableCell>
              <TableCell sx={headStyle}>Subcontrato</TableCell>
              <TableCell sx={headStyle}>Fecha Fin. Inicial</TableCell>
              <TableCell sx={headStyle}>Unidad</TableCell>
              <TableCell sx={headStyle}>Cantidad</TableCell>
              <TableCell sx={headStyle}>Acum. Prog. Inicial</TableCell>
              <TableCell sx={headStyle}>Desvío Prog. Inicial</TableCell>
              <TableCell sx={headStyle}>Ejec. S-3</TableCell>
              <TableCell sx={headStyle}>Ejec. S-2</TableCell>
              <TableCell sx={headStyle}>Ejec. S-1</TableCell>
              <TableCell sx={headStyle}>Acum. anterior</TableCell>
              <TableCell sx={headStyle}>Ejec. periodo</TableCell>
              <TableCell sx={headStyle}>Acum. Actual</TableCell>
              <TableCell sx={headStyle}>Avance a la fecha</TableCell>
              <TableCell sx={headStyle}>Prog. periodo</TableCell>
              <TableCell sx={headStyle}>Desv. periodo</TableCell>
              <TableCell sx={headStyle}>Cuad. Ejec.</TableCell>
              <TableCell sx={headStyle}>Cuad. Plan</TableCell>
              <TableCell sx={headStyle}>Plan S+1</TableCell>
              <TableCell sx={headStyle}>Plan S+2</TableCell>
              <TableCell sx={headStyle}>Plan S+3</TableCell>
              <TableCell sx={headStyle}>Acum. Plan S+3</TableCell>
              <TableCell sx={headStyle}>Nva. Fecha Fin.</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {dataRas.map((ras, idx) => (
              <TableRow
                key={idx}
                hover
                sx={{ "&:hover td": { backgroundColor: "#e3f2fd !important" } }}
              >
                <TableCell sx={bodyStyle}>{idx + 1}</TableCell>
                <TableCell sx={bodyStyle}>{ras.section ?? ""}</TableCell>
                <TableCell sx={bodyActivity}>
                  {ras.name ?? ""} {ras.subActivity ? `(${ras.subActivity})` : ""}
                </TableCell>
                {/* C - Subcontrato (Tier 3) */}
                <TableCell sx={bodyPending}>{ras.subcontract ?? PENDING}</TableCell>
                {/* D - Fecha Finalización Inicial (Tier 3) */}
                <TableCell sx={bodyPending}>{ras.initialFinishDate ?? PENDING}</TableCell>
                <TableCell sx={bodyStyle}>{ras.unit}</TableCell>
                <TableCell sx={bodyStyle}>{fmt(ras.totalScope)}</TableCell>
                {/* G - Acumulado Programa Inicial (Tier 3) */}
                <TableCell sx={bodyPending}>
                  {ras.initialProgramAccum != null ? fmt(ras.initialProgramAccum) : PENDING}
                </TableCell>
                {/* I - Desvío Programa Inicial (Tier 3) */}
                <TableCell sx={bodyPending}>
                  {ras.initialProgramDeviation != null ? fmt(ras.initialProgramDeviation) : PENDING}
                </TableCell>
                {/* J/K/L - Ejecutado S-3 / S-2 / S-1 */}
                <TableCell sx={bodyStyle}>{fmt(ras.executedS3)}</TableCell>
                <TableCell sx={bodyStyle}>{fmt(ras.executedS2)}</TableCell>
                <TableCell sx={bodyStyle}>{fmt(ras.executedS1)}</TableCell>
                {/* Acumulado anterior (columna conservada) */}
                <TableCell sx={bodyStyle}>{fmt(ras.previousExecuted)}</TableCell>
                {/* M/N/O/P/Q */}
                <TableCell sx={bodyStyle}>{fmt(ras.currentExecuted)}</TableCell>
                <TableCell sx={bodyStyle}>{fmt(ras.totalProgress)}</TableCell>
                <TableCell sx={bodyStyle}>{Math.floor(ras.activityProgress ?? 0)}%</TableCell>
                <TableCell sx={bodyStyle}>{fmt(ras.programmedAdvance)}</TableCell>
                <TableCell sx={bodyStyle}>{fmt(ras.weeklyDeviation)}</TableCell>
                {/* R/S - Cuadrillas */}
                <TableCell sx={bodyStyle}>{fmt(ras.executedCrews)}</TableCell>
                <TableCell sx={bodyStyle}>{fmt(ras.plannedCrews)}</TableCell>
                {/* T/U/V/W - Plan futuro */}
                <TableCell sx={bodyStyle}>{fmt(ras.planS1)}</TableCell>
                <TableCell sx={bodyStyle}>{fmt(ras.planS2)}</TableCell>
                <TableCell sx={bodyStyle}>{fmt(ras.planS3)}</TableCell>
                <TableCell sx={bodyStyle}>{fmt(ras.planAccumS3)}</TableCell>
                {/* X - Nva. Fecha Finalización (Tier 3) */}
                <TableCell sx={bodyPending}>{ras.newFinishDate ?? PENDING}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Grid>
  );
};

export default RasReportTable;
